import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import { comics, categories } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const displayName = category?.name || '全部分类';
  const description = category?.description || '';

  const categoryComics = comics.filter((c) => c.category === slug);

  // If no specific slug, show all comics
  const displayComics = slug ? categoryComics : comics;

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        name: `${displayName} - 韩漫画免费阅读`,
        description: `${displayName}分类下的韩国漫画大全，${description}，韩漫画免费阅读，下拉式观看体验。`,
        isPartOf: { '@type': 'WebSite', name: 'ManhwaFun' },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'http://costrebate.com/' },
          { '@type': 'ListItem', position: 2, name: displayName, item: `http://costrebate.com/category/${slug}` },
        ],
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${displayName} - 韩漫画免费阅读_好看的韩国漫画大全`}
        description={`${displayName}分类下的韩国漫画大全，${description}。韩漫画免费阅读，看漫画下拉式，更新及时体验流畅。`}
        keywords={`${displayName},韩漫画免费阅读,看漫画下拉式,好看的韩国漫画大全,韩国漫画网站`}
        canonical={`http://costrebate.com/category/${slug}`}
        ogTitle={`${displayName} - 韩漫画免费阅读`}
        ogDescription={`${displayName}分类下的韩国漫画大全，${description}。韩漫画免费阅读，看漫画下拉式。`}
        ogUrl={`http://costrebate.com/category/${slug}`}
        schemaData={schemaData}
      />

      <Navbar transparent={false} />

      <main className="pt-16 md:pt-18">
        {/* Breadcrumb */}
        <div className="bg-background-50 border-b border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
            <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-400" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700 font-medium">{displayName}</span>
            </nav>
          </div>
        </div>

        {/* Category Header */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
              <div>
                <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950">
                  {displayName}
                </h1>
                <p className="mt-2 text-sm text-foreground-500">
                  {description} · 共 {displayComics.length} 部作品
                </p>
              </div>
            </div>

            {/* Category Tabs */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors duration-200 ${
                    slug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-200 text-foreground-600 hover:bg-background-300'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {/* Comics Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
              {displayComics.map((comic) => (
                <ComicCard key={comic.id} {...comic} />
              ))}
            </div>

            {displayComics.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 mb-4">
                  <i className="ri-book-open-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无漫画，请浏览其他分类</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}